import React from 'react'
import {UnionConfiguration} from '../scheduling/UnionConfiguration'
import type {
  AccessibleLocation,
  Organization,
  WorkerOrgRelationship,
} from '../../../types/access'
import { useI18n } from '../../../i18n/I18nContext'
import { ScheduleCalendarView } from './ScheduleCalendarView'
import { useIndustryTerminology } from '../../../hooks/useIndustryTerminology'

interface SchedulePanelProps {
  organizations: Organization[]
  accessibleLocations: AccessibleLocation[]
  workerRelationships: WorkerOrgRelationship[]
  selectedOrgId: string
  canViewSchedule: boolean
  canManageJobs: boolean
  canManageApplications: boolean
  canManageAssignments: boolean
  canViewPayRate: boolean
  canViewBillRate: boolean
  onOpenJob: (jobId: string) => void
  onOpenWorkerProfile: (workerProfileId: string) => void
}

export const SchedulePanel: React.FC<SchedulePanelProps> = ({
  organizations,
  accessibleLocations,
  workerRelationships,
  selectedOrgId,
  canViewSchedule,
  canManageJobs,
  canManageApplications,
  canManageAssignments,
  canViewPayRate,
  canViewBillRate,
  onOpenJob,
  onOpenWorkerProfile,
}) => {
  const { t } = useI18n()
  const industryLabels = useIndustryTerminology()
  const shiftsLabel = industryLabels.explicitIndustry ? industryLabels.label('shifts').toLowerCase() : t('shifts')
  const jobsLabel = industryLabels.explicitIndustry ? industryLabels.label('jobs').toLowerCase() : t('jobs/orders')

  if (!canViewSchedule) {
    return <p className="admin-disabled-callout">{t('Schedule access requires job, shift, application, or assignment view permission.')}</p>
  }

  return (
    <div className="stack" data-testid="admin-schedule-panel">
      <UnionConfiguration organizations={organizations} relationships={workerRelationships}/>
      <div className="admin-section-header">
        <div>
          <h3>{t('Dated execution workflow')}</h3>
          <p className="text-muted">
            {t('Schedule is the dated execution layer for {jobs}. Use it to create, publish, assign, and monitor {shifts} without duplicating the {jobs} list.', {
              jobs: jobsLabel,
              shifts: shiftsLabel,
            })}
          </p>
        </div>
      </div>

      <ScheduleCalendarView
        organizations={organizations}
        accessibleLocations={accessibleLocations}
        workerRelationships={workerRelationships}
        selectedOrgId={selectedOrgId}
        canViewSchedule={canViewSchedule}
        canManageJobs={canManageJobs}
        canManageApplications={canManageApplications}
        canManageAssignments={canManageAssignments}
        canViewPayRate={canViewPayRate}
        canViewBillRate={canViewBillRate}
        onOpenJob={onOpenJob}
        onOpenWorkerProfile={onOpenWorkerProfile}
      />
    </div>
  )
}
